import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isEqualFormValues } from 'dpl/shared/form/utils';
import { COMMON_LABEL_CLASSES } from 'dpl/shared/form/utils/constants';

import ErrorWrapper from './ErrorWrapper';
import RadioButton from './RadioButton';

const TABS_VARIANTS = ['tabs', 'tabs-justified'];

const DEFAULT_CLASSES = {
  legend: 'mb1',
  radioButton: 'mb1'
};

const simpleRadioButtonGroupDefaultPropErrors = [];
/** @param {any} props */
export default function SimpleRadioButtonGroup({
  displayErrors = false,
  errors = simpleRadioButtonGroupDefaultPropErrors,
  label = null,
  name,
  onChange = null,
  options,
  value = null,
  inline = false,
  newDesignSystemStyles = false,
  variant = null,
  wrappedLastOption = false,
  inputFirst = false,
  classes: initialClasses = DEFAULT_CLASSES
}) {
  const isTabsVariant = TABS_VARIANTS.includes(variant);
  const classes = { ...DEFAULT_CLASSES, ...initialClasses };

  return (
    <div className="RadioButtonGroup">
      <ErrorWrapper isShown={displayErrors} errors={errors}>
        <fieldset>
          {label && (
            <legend
              className={classnames(
                'RadioButtonGroup__legend',
                COMMON_LABEL_CLASSES,
                classes.legend
              )}
            >
              {label}
            </legend>
          )}
          <ul className="RadioButtonGroup__options">
            {options.map((o, idx) => (
              <li
                className={classnames(
                  {
                    'dib mr6': inline && !wrappedLastOption,
                    'dib mr6 mb4':
                      inline && wrappedLastOption && idx < options.length - 1,
                    'ph4 pv4 ba': isTabsVariant,
                    'b--default':
                      isTabsVariant && !isEqualFormValues(value, o.value),
                    'b--primary':
                      isTabsVariant && isEqualFormValues(value, o.value)
                  },
                  // Inline and tabs variants space themselves horizontally
                  !inline && !isTabsVariant && classes.radioButton
                )}
                key={o.value}
              >
                <RadioButton
                  checked={isEqualFormValues(value, o.value)}
                  id={`${name}-${o.value}`}
                  name={name}
                  onChange={onChange}
                  title={o.title}
                  subtitle={o.subtitle}
                  value={o.value}
                  variant={variant}
                  newDesignSystemStyles={newDesignSystemStyles}
                  inputFirst={inputFirst}
                />
              </li>
            ))}
          </ul>
        </fieldset>
      </ErrorWrapper>
    </div>
  );
}

SimpleRadioButtonGroup.propTypes = {
  displayErrors: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.node),
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool
      ]).isRequired
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  inline: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool,
  // Makes it so the last option is on its own line when inline is true
  wrappedLastOption: PropTypes.bool,
  variant: PropTypes.string,
  inputFirst: PropTypes.bool,
  classes: PropTypes.shape({
    legend: PropTypes.string,
    radioButton: PropTypes.string
  })
};
